import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Seo from "@/components/feature/Seo";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import useHashScroll from "@/hooks/useHashScroll";
import { articles, hotArticles } from "@/mocks/articles";
import ArticleDoubts from "@/pages/article/components/ArticleDoubts";
import ArticleFigures from "@/pages/article/components/ArticleFigures";
import ArticleTimeline from "@/pages/article/components/ArticleTimeline";
import ArticleToc from "@/pages/article/components/ArticleToc";
import RelatedArticles from "@/pages/article/components/RelatedArticles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  useHashScroll();

  const article = useMemo(() => articles.find((item) => item.slug === slug), [slug]);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter(
      (item) => item.categorySlug === article.categorySlug && item.id !== article.id
    );
    const fill = hotArticles.filter(
      (item) =>
        item.id !== article.id &&
        !sameCategory.some((picked) => picked.id === item.id)
    );
    return [...sameCategory, ...fill].slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-search-line text-3xl" aria-hidden="true" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">
            这篇解读不存在或已调整
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            它可能已经更换了链接。你可以回到解读库，用关键词重新找到它。
          </p>
          <Link
            to="/analysis"
            className="mt-7 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            返回深度解读库
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const tocItems = [
    { id: "overview", label: "事件概览" },
    { id: "timeline", label: "时间线" },
    { id: "figures", label: "关键角色" },
    { id: "doubts", label: "疑点清单" },
    { id: "analysis", label: "逻辑复盘" },
    { id: "conclusion", label: "一句话结论" },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Seo
        title={`${article.title} - 事件深度解读 | 15黑料网`}
        description={`${article.summary.slice(0, 78)}…15黑料网从时间线、关键角色与疑点清单三个角度复盘该事件。`}
        keywords={`${article.title},${article.category},事件解读,内幕解析,真相挖掘`}
        canonicalPath={`/analysis/${article.slug}`}
        ogType="article"
        ogImage={article.cover}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.summary,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            inLanguage: "zh-CN",
            articleSection: article.category,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: "15黑料网编辑部" },
            publisher: {
              "@type": "Organization",
              name: "15黑料网",
              url: "https://sznita.cn/",
            },
            mainEntityOfPage: `https://sznita.cn/analysis/${article.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://sznita.cn/" },
              { "@type": "ListItem", position: 2, name: "深度解读", item: "https://sznita.cn/analysis" },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://sznita.cn/analysis/${article.slug}`,
              },
            ],
          },
        ]}
      />
      <SiteHeader />

      <main className="w-full flex-1">
        <article>
          <header className="w-full border-b border-background-200 bg-background-100/60">
            <div className="mx-auto w-full max-w-[1400px] px-4 pb-10 pt-8 md:px-8 md:pb-12 md:pt-10">
              <Breadcrumbs
                items={[
                  { label: "深度解读", path: "/analysis" },
                  { label: article.category, path: `/topic/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/topic/${article.categorySlug}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="flex items-center gap-1 text-xs text-accent-700">
                  <i className="ri-fire-line" aria-hidden="true" />
                  热度 {article.heat}
                </span>
              </div>
              <h1 className="mt-5 max-w-4xl font-heading text-3xl font-black leading-snug text-foreground-950 md:text-[2.6rem]">
                {article.title}
              </h1>
              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {article.summary}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-user-line" aria-hidden="true" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-time-line" aria-hidden="true" />
                  {article.readTime}
                </span>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </header>

          <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-12">
            <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 sm:h-80 md:h-[420px]">
              <img
                src={article.cover}
                alt={`${article.title} - ${article.category}事件深度解读封面图`}
                title={`${article.title} - 15黑料网事件复盘`}
                className="h-full w-full object-top object-cover"
              />
            </div>

            <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <section id="overview" className="scroll-mt-24">
                  <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    事件概览
                  </h2>
                  <div className="prose-article mt-5 max-w-none">
                    {article.intro.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </section>

                <section id="timeline" className="mt-14 scroll-mt-24">
                  <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    时间线还原
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                    以下节点均来自公开可查证的信息，按事件发生顺序排列。排序本身就是一种信息。
                  </p>
                  <ArticleTimeline items={article.timeline} />
                </section>

                <section id="figures" className="mt-14 scroll-mt-24">
                  <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    关键角色
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                    我们关注的是每个角色在节点上「做了什么」，而不是他们「说了什么立场」。
                  </p>
                  <ArticleFigures items={article.keyFigures} />
                </section>

                <section id="doubts" className="mt-14 scroll-mt-24">
                  <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    还没解释清楚的疑点
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                    比结论更重要的是问题本身。以下几条是公开信息里仍然存在缺口的地方。
                  </p>
                  <ArticleDoubts items={article.doubts} />
                </section>

                <section id="analysis" className="mt-14 scroll-mt-24">
                  <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    逻辑复盘
                  </h2>
                  <div className="mt-6 space-y-8">
                    {article.analysis.map((section) => (
                      <div key={section.heading}>
                        <h3 className="font-heading text-lg font-bold text-foreground-950">
                          {section.heading}
                        </h3>
                        <div className="prose-article mt-4">
                          {section.paragraphs.map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section id="conclusion" className="mt-14 scroll-mt-24">
                  <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    一句话结论
                  </h2>
                  <div className="mt-5 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-6">
                    <p className="font-heading text-base leading-relaxed text-foreground-900 md:text-lg">
                      {article.conclusion}
                    </p>
                  </div>
                </section>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24">
                  <ArticleToc items={tocItems} />
                  <div className="mt-6 rounded-lg border border-background-200 bg-secondary-50 p-5">
                    <p className="label-caps text-[11px] text-primary-600">Editor's Note</p>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                      本文只基于公开信息做结构梳理，涉及具体个人处均已做匿名化处理。若你有更接近一手材料的补充，欢迎随时联系编辑部。
                    </p>
                    <a
                      href="mailto:editor@15heiliao.com"
                      className="mt-4 inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
                    >
                      <i className="ri-mail-send-line" aria-hidden="true" />
                      提供线索
                    </a>
                  </div>
                </div>
              </aside>
            </div>

            <div className="mt-16">
              <RelatedArticles items={related} />
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}